import PropTypes from "prop-types";

import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import ALPHABET from "@/view/constants/alphabet";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import {
  choiceLetterCss,
  choiceTextCss,
  containerCss,
  multipleChoiceBackgroundCss,
  multipleChoiceContainerCss,
  multipleChoiceCss,
  multipleChoiceLabelCss,
} from "./MultipleChoice.style";

const propTypes = {
  /**
   * The position of the option
   */
  choiceIndex: PropTypes.number.isRequired,
  /**
   * The string containing the primary choice text
   */
  choiceText: PropTypes.string.isRequired,
  /**
   * Each array of options contains a single correct response.
   * That 'is_correct' state is passed here.
   */
  isCorrectChoice: PropTypes.bool,
  /**
   * Maps to returned user data.
   * The student selected this choice while completing the quiz.
   */
  isStudentsChoice: PropTypes.bool,
  /**
   * Bool that hides the correct answers.
   */
  shouldHideQuizAnswers: PropTypes.bool,
};

const MultipleChoice = (props) => {
  const {
    choiceIndex,
    choiceText,
    isCorrectChoice = false,
    isStudentsChoice = false,
    shouldHideQuizAnswers = false,
  } = props;

  const { isReadAloudModeActive } = useReadAloudMode();

  const [readAloudQuizzesVariation] =
    useGetUserExperimentSegment("read_aloud_quizzes");

  const shouldHideCorrectAnswerLabel =
    !isCorrectChoice || shouldHideQuizAnswers;
  const shouldHideLabel = !isStudentsChoice && shouldHideCorrectAnswerLabel;

  const renderLabel = () => {
    if (shouldHideLabel) {
      return null;
    }

    const labelProps = {
      labelColor: isCorrectChoice ? "success" : "primary",
      status: isCorrectChoice ? "correct" : "primary",
      text:
        isCorrectChoice && !isStudentsChoice ? "Correct answer" : "Your answer",
    };

    return (
      <ActivityChoiceIndicator
        __cssFor={multipleChoiceLabelCss}
        labelColor={labelProps.labelColor}
        status={labelProps.status}
        text={labelProps.text}
      />
    );
  };

  const isReadAloudButtonActive =
    isReadAloudModeActive && readAloudQuizzesVariation === "on";

  return (
    <li css={containerCss}>
      <div
        css={[
          multipleChoiceContainerCss(isReadAloudButtonActive),
          multipleChoiceBackgroundCss(isCorrectChoice, shouldHideLabel),
        ]}
      >
        {renderLabel()}
        <div css={multipleChoiceCss}>
          <span css={choiceLetterCss}>
            {ALPHABET.toUpperCase()[choiceIndex]}
          </span>
          <span css={choiceTextCss}>{choiceText}</span>
        </div>
      </div>
    </li>
  );
};

MultipleChoice.propTypes = propTypes;

export default MultipleChoice;
